import React, { useCallback, useRef, useState } from 'react';
import { CalendarRangeIcon, ChevronDownIcon } from 'lucide-react';
import { differenceInCalendarDays, format, isBefore, parseISO } from 'date-fns';
import { useDismiss } from '../hooks/useDismiss';
import { DropdownPanel } from './DropdownPanel';
import { DateRangeCalendar } from './DateRangeCalendar';
import { TypingCaret } from './TypingCaret';
type DateRangeFieldProps = {
  id: string;
  startDate: string; // yyyy-MM-dd or ''
  endDate: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  placeholder?: string;
  endPlaceholder?: string;
  bold?: boolean; // heavier underline
  // Optional flexible mode: quick-pick of the coming months instead of exact dates
  months?: string[];
  onMonthsChange?: (months: string[]) => void;
  // Intro typewriter: text being typed in place of the value, and whether the caret shows
  typed?: string | null;
  typing?: boolean;
  onInteract?: () => void;
};

// The next six months, e.g. "Nov 2026"
const upcomingMonths = (() => {
  const now = new Date();
  return Array.from({
    length: 6
  }, (_, i) => new Date(now.getFullYear(), now.getMonth() + i, 1));
})();
const toISO = (d: Date) => format(d, 'yyyy-MM-dd');
const parse = (v: string) => v ? parseISO(v) : null;

// A single field that opens an arrival / departure calendar
export function DateRangeField({
  id,
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  placeholder = 'Arrive — Depart',
  endPlaceholder = 'Depart',
  bold = false,
  months = [],
  onMonthsChange,
  typed,
  typing,
  onInteract
}: DateRangeFieldProps) {
  const isTyping = typed !== null && typed !== undefined;
  const flexible = months.length > 0;
  const toggleMonth = (key: string) => {
    if (!onMonthsChange) return;
    // Choosing months replaces exact dates
    onStartChange('');
    onEndChange('');
    onMonthsChange(months.includes(key) ? months.filter(m => m !== key) : [...months, key]);
  };
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(rootRef, open, close);
  const start = parse(startDate);
  const end = parse(endDate);
  const pick = (day: Date) => {
    if (flexible) onMonthsChange?.([]);
    if (!start || end || isBefore(day, start)) {
      onStartChange(toISO(day));
      onEndChange('');
      return;
    }
    if (differenceInCalendarDays(day, start) === 0) return;
    onEndChange(toISO(day));
  };
  const nights = start && end ? differenceInCalendarDays(end, start) : 0;
  const label = flexible ? months.length <= 2 ? months.join(', ') : `${months[0]} + ${months.length - 1} more` : start ? `${format(start, 'd MMM')} — ${end ? format(end, 'd MMM') : endPlaceholder}` : placeholder;
  const status = flexible ? `Flexible · ${months.length} ${months.length === 1 ? 'month' : 'months'}` : !start ? 'Choose your arrival' : !end ? 'Now choose your departure' : `${nights} ${nights === 1 ? 'night' : 'nights'}`;
  const hasValue = flexible || Boolean(start);
  return <div ref={rootRef} className="relative" data-id="element-324" data-mp="3co8jnw2idqct">
      <button id={id} type="button" onClick={() => {
      onInteract?.();
      setOpen(o => !o);
    }} aria-haspopup="dialog" aria-expanded={open} aria-label={`Dates: ${hasValue ? label : 'not chosen'}`} className={`relative flex w-full items-center gap-2.5 ${bold ? 'border-b-[1.5px]' : 'border-b'} border-ink pb-3 pr-8 pt-1.5 text-left text-[18px] focus:outline-none ${hasValue || isTyping ? 'text-ink' : 'font-light text-ink/60'}`} data-id="element-325" data-mp="37m9dkefdwfc6">
        <CalendarRangeIcon className="h-[18px] w-[18px] shrink-0 text-ink/80" strokeWidth={1.25} aria-hidden="true" data-id="element-326" data-mp="3lmk4tiklvvxv" />
        {isTyping ? <span className="flex min-w-0 items-center truncate" data-id="element-327" data-mp="i6de0cnaht6i">
            {typed}
            {typing && <TypingCaret data-id="element-328" data-mp="2odfcaybot3j" />}
          </span> : <span className="truncate" data-id="element-329" data-mp="2miwd26m3b23">{label}</span>}
        <ChevronDownIcon className={`pointer-events-none absolute right-0 top-3 h-4 w-4 transition-transform duration-300 ease-luxe ${open ? 'rotate-180' : ''}`} strokeWidth={1.25} aria-hidden="true" data-id="element-330" data-mp="7nglxztyb7dm" />
      </button>

      <DropdownPanel open={open} className="left-0 w-[min(340px,calc(100vw-48px))] md:w-[640px]" data-id="element-331" data-mp="3twhkpfqh41zp">
        <div role="dialog" aria-label="Choose your dates" data-id="element-332" data-mp="lgobllyh1sso">
          {onMonthsChange && <div className="border-b border-line px-5 pb-4 pt-4" data-id="element-333" data-mp="qfr82i0dyqp1">
              <p className="text-[13px] font-medium" data-id="element-334" data-mp="bnqnn0apnbmo">Flexible? Choose months</p>
              <div className="mt-3 grid grid-cols-3 gap-2 md:grid-cols-6" role="group" aria-label="Flexible months" data-id="element-335" data-mp="gnd6pqd7zm3e">
                {upcomingMonths.map(d => {
              const key = format(d, 'MMM yyyy');
              const on = months.includes(key);
              return <button key={key} type="button" onClick={() => toggleMonth(key)} aria-pressed={on} className={`border px-2 py-2 text-center transition-colors duration-150 ${on ? 'border-ink bg-ink text-paper' : 'border-line hover:border-ink'}`} data-id="element-336" data-mp="2tu1oo0xmochp">
                      <span className="block text-[14px] font-medium" data-id="element-337" data-mp="2awicwpjscfrl">{format(d, 'MMM')}</span>
                      <span className={`block text-[11px] font-light ${on ? 'text-paper/70' : 'text-ink/60'}`} data-id="element-338" data-mp="25xxij6r7sqb2">{format(d, 'yyyy')}</span>
                    </button>;
            })}
              </div>
              <p className="mt-4 text-[13px] font-light text-ink/60" data-id="element-339" data-mp="2ftig8sp934f0">Or exact dates</p>
            </div>}
          <DateRangeCalendar start={start} end={end} onPick={pick} data-id="element-340" data-mp="3vdeu9nwfpvh7" />
          <div className="flex items-center justify-between gap-4 border-t border-line px-5 py-3" data-id="element-341" data-mp="3r9fghd2jharj">
            <p className="text-[14px] font-light text-ink/70" aria-live="polite" data-id="element-342" data-mp="2nyjgr56022o6">
              {status}
            </p>
            <div className="flex items-center gap-5" data-id="element-343" data-mp="2t08uwtoiqwgh">
              <button type="button" onClick={() => {
              onStartChange('');
              onEndChange('');
              onMonthsChange?.([]);
            }} className="text-[14px] font-light text-ink/70 transition-opacity duration-150 hover:opacity-60" data-id="element-344" data-mp="2xyad7ce4erqw">
                Clear
              </button>
              <button type="button" onClick={close} className="bg-ink px-5 py-2 text-[14px] font-medium text-paper transition-opacity duration-150 hover:opacity-85" data-id="element-345" data-mp="2e8zxvziae8tb">
                Done
              </button>
            </div>
          </div>
        </div>
      </DropdownPanel>
    </div>;
}